<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnel: {
    type: Object,
    default: () => ({}),
  },
  stages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'close',
  'winDeal',
  'loseDeal',
  'update',
  'moveToStage',
]);

const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');
const { currency, currencySymbol } = useCurrency();

// State
const isLoading = ref(false);
const isEditingValue = ref(false);
const isEditingProbability = ref(false);
const isEditingExpectedClose = ref(false);
const editedValue = ref(0);
const editedProbability = ref(50);
const editedExpectedClose = ref('');
const newNote = ref('');
const isAddingNote = ref(false);
const activities = ref([]);
const isLoadingActivities = ref(false);
const showStageDropdown = ref(false);

// Computed
const contact = computed(() => {
  return props.conversation?.contact || props.conversation?.meta?.sender || {};
});

const contactName = computed(() => {
  return contact.value?.name || 'Cliente';
});

const contactEmail = computed(() => {
  return contact.value?.email || '';
});

const contactPhone = computed(() => {
  return contact.value?.phone_number || contact.value?.phone || '';
});

const contactAvatar = computed(() => {
  return (
    contact.value?.avatar_url ||
    contact.value?.avatar ||
    contact.value?.thumbnail ||
    ''
  );
});

const contactInitials = computed(() => {
  return (contactName.value || 'U')
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

const dealValue = computed(() => {
  // Priority 1: Deal value from mapping
  if (
    props.mapping?.deal_value !== null &&
    props.mapping?.deal_value !== undefined &&
    props.mapping?.deal_value !== ''
  ) {
    return parseFloat(props.mapping.deal_value) || 0;
  }

  // Priority 2: Flat deal_value on the kanban conversation JSON
  if (
    props.conversation?.deal_value !== null &&
    props.conversation?.deal_value !== undefined &&
    props.conversation?.deal_value !== ''
  ) {
    return parseFloat(props.conversation.deal_value) || 0;
  }

  // Priority 3: Deal from conversation
  if (props.conversation?.deal?.amount) {
    return parseFloat(props.conversation.deal.amount) || 0;
  }

  // Priority 3: Sum of products
  const products = props.conversation?.conversation_products || [];
  return products.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
});

const formattedDealValue = computed(() => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.value,
  }).format(dealValue.value);
});

const probability = computed(() => {
  // Get from mapping metadata or stage config
  if (props.mapping?.metadata?.probability) {
    return props.mapping.metadata.probability;
  }

  // Get from stage config
  const stageConfig = props.funnel?.stages?.[props.mapping?.stage_name];
  return stageConfig?.default_probability || 50;
});

const dealStatus = computed(() => {
  return (
    props.mapping?.deal_status || props.conversation?.deal_status || 'open'
  );
});

const dealStatusConfig = computed(() => {
  const configs = {
    open: {
      label: t('KANBAN.DEAL.STATUS.OPEN'),
      class: 'bg-n-blue-3 text-n-blue-text',
      icon: 'i-lucide-focus',
    },
    won: {
      label: t('KANBAN.DEAL.STATUS.WON'),
      class: 'bg-n-teal-3 text-n-teal-11',
      icon: 'i-lucide-trophy',
    },
    lost: {
      label: t('KANBAN.DEAL.STATUS.LOST'),
      class: 'bg-n-ruby-3 text-n-ruby-11',
      icon: 'i-lucide-x-circle',
    },
  };
  return configs[dealStatus.value] || configs.open;
});

const currentStageName = computed(() => {
  return props.mapping?.stage_name || '';
});

const currentStageConfig = computed(() => {
  return props.funnel?.stages?.[currentStageName.value] || {};
});

const currentStageLabel = computed(() => {
  return currentStageConfig.value?.label || currentStageName.value;
});

const products = computed(() => {
  return props.conversation?.conversation_products || [];
});

const checklist = computed(() => {
  return props.mapping?.checklist || [];
});

const checklistProgress = computed(() => {
  const done = checklist.value.filter(item => item.done).length;
  return {
    done,
    total: checklist.value.length,
    percentage: checklist.value.length
      ? Math.round((done / checklist.value.length) * 100)
      : 0,
  };
});

const assignedAgents = computed(() => {
  const agents = [];

  // Primary assignee
  if (props.conversation?.assignee) {
    agents.push({
      ...props.conversation.assignee,
      role: 'primary',
    });
  }

  // Additional agents from mapping
  if (props.mapping?.assigned_agents?.length) {
    props.mapping.assigned_agents.forEach(agent => {
      if (!agents.find(a => a.id === agent.id)) {
        agents.push(agent);
      }
    });
  }

  return agents;
});

const stageHistory = computed(() => {
  return props.mapping?.metadata?.stage_history || [];
});

// Normalize date values: handles Unix seconds (number), Unix ms, and ISO8601 strings
const normalizeDate = value => {
  if (!value) return null;
  // Unix timestamp in seconds (e.g. 1739119863) - under year 5000 in seconds
  if (typeof value === 'number' && value < 4102444800) {
    return new Date(value * 1000);
  }
  return new Date(value);
};

// Deal creation date and age
// Priority: mapping_created_at (when deal was added to funnel) > conversation.created_at
const dealCreatedAt = computed(() => {
  // Check multiple possible sources for the creation date
  return (
    props.conversation?.mapping_created_at ||
    props.mapping?.created_at ||
    props.conversation?.created_at
  );
});

const dealAge = computed(() => {
  if (!dealCreatedAt.value) return { days: 0, label: '', status: 'healthy' };

  const created = normalizeDate(dealCreatedAt.value);
  const now = new Date();
  const diffMs = now - created;
  const days = Math.floor(diffMs / 86400000);

  let status = 'healthy';
  if (days > 30) status = 'critical';
  else if (days > 14) status = 'danger';
  else if (days > 7) status = 'warning';

  return { days, status };
});

const dealAgeConfig = computed(() => {
  const configs = {
    healthy: {
      class: 'bg-emerald-500/10 text-emerald-500',
      icon: 'i-lucide-clock',
    },
    warning: {
      class: 'bg-amber-500/10 text-amber-500',
      icon: 'i-lucide-alert-triangle',
    },
    danger: {
      class: 'bg-orange-500/10 text-orange-500',
      icon: 'i-lucide-alert-circle',
    },
    critical: {
      class: 'bg-red-500/10 text-red-500',
      icon: 'i-lucide-flame',
    },
  };
  return configs[dealAge.value.status] || configs.healthy;
});

// Expected close date (top-level column, with metadata fallback)
const expectedCloseDate = computed(() => {
  return (
    props.conversation?.expected_close_date ||
    props.mapping?.expected_close_date ||
    props.mapping?.metadata?.expected_close_date ||
    null
  );
});

// Won reason (for won deals)
const wonReason = computed(() => {
  if (dealStatus.value !== 'won') return null;
  return (
    props.conversation?.deal_won_reason ||
    props.mapping?.deal_won_reason ||
    null
  );
});

const isOverdue = computed(() => {
  if (!expectedCloseDate.value) return false;
  return new Date(expectedCloseDate.value) < new Date();
});

// Tags/Labels
const dealTags = computed(() => {
  return props.conversation?.labels || [];
});

// Loss reason (for lost deals)
const lossReason = computed(() => {
  if (dealStatus.value !== 'lost') return null;
  return props.mapping?.metadata?.loss_reason || null;
});

const lossNotes = computed(() => {
  if (dealStatus.value !== 'lost') return null;
  return props.mapping?.metadata?.loss_notes || null;
});

// Available stages for quick move
const availableStages = computed(() => {
  if (!props.stages?.length) return [];
  return props.stages.filter(s => s.name !== currentStageName.value);
});

// Methods
const handleClose = () => {
  emit('close');
};

const startEditValue = () => {
  editedValue.value = dealValue.value;
  isEditingValue.value = true;
};

const cancelEditValue = () => {
  isEditingValue.value = false;
};

const saveValue = async () => {
  try {
    isLoading.value = true;

    await KanbanAPI.updateDealValue(
      props.funnel.id,
      props.conversation.id,
      editedValue.value
    );

    isEditingValue.value = false;
    useAlert(t('KANBAN.DEAL.VALUE_UPDATED'));
    emit('update');
  } catch (error) {
    // Error handled by useAlert
    useAlert(t('KANBAN.DEAL.VALUE_UPDATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const startEditProbability = () => {
  editedProbability.value = probability.value;
  isEditingProbability.value = true;
};

const cancelEditProbability = () => {
  isEditingProbability.value = false;
};

const saveProbability = async () => {
  try {
    isLoading.value = true;

    await KanbanAPI.updateDealProbability(
      props.funnel.id,
      props.conversation.id,
      editedProbability.value
    );

    isEditingProbability.value = false;
    useAlert(t('KANBAN.DEAL.PROBABILITY_UPDATED'));
    emit('update');
  } catch (error) {
    // Error handled by useAlert
    useAlert(t('KANBAN.DEAL.PROBABILITY_UPDATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleWinDeal = () => {
  emit('winDeal', {
    conversation: props.conversation,
    mapping: props.mapping,
    currentValue: dealValue.value,
  });
};

const handleLoseDeal = () => {
  emit('loseDeal', {
    conversation: props.conversation,
    mapping: props.mapping,
  });
};

// Expected close date methods
const startEditExpectedClose = () => {
  editedExpectedClose.value = expectedCloseDate.value || '';
  isEditingExpectedClose.value = true;
};

const cancelEditExpectedClose = () => {
  isEditingExpectedClose.value = false;
};

const saveExpectedClose = async () => {
  try {
    isLoading.value = true;

    await KanbanAPI.updateDealMetadata(props.funnel.id, props.conversation.id, {
      expected_close_date: editedExpectedClose.value,
    });

    isEditingExpectedClose.value = false;
    useAlert(t('KANBAN.DEAL.EXPECTED_CLOSE_UPDATED'));
    emit('update');
  } catch (error) {
    useAlert(t('KANBAN.DEAL.EXPECTED_CLOSE_UPDATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// PIX charge state
const showPixModal = ref(false);
const isCreatingCharge = ref(false);
const pixValue = ref(0);
const pixDueDate = ref('');
const pixDescription = ref('');
const pixResult = ref(null);
const pixNotConfigured = ref(false);
const pixErrorMessage = ref('');

const openPixModal = () => {
  pixValue.value = dealValue.value || 0;
  pixDueDate.value = '';
  pixDescription.value = '';
  pixResult.value = null;
  pixNotConfigured.value = false;
  pixErrorMessage.value = '';
  showPixModal.value = true;
};

const closePixModal = () => {
  showPixModal.value = false;
};

const createPixCharge = async () => {
  if (!pixValue.value || pixValue.value <= 0) {
    useAlert(t('KANBAN.DEAL.PIX.INVALID_VALUE'));
    return;
  }

  try {
    isCreatingCharge.value = true;
    pixNotConfigured.value = false;
    pixErrorMessage.value = '';

    const response = await KanbanAPI.createDealPayment(props.funnel.id, {
      conversationId: props.conversation.id,
      value: parseFloat(pixValue.value),
      dueDate: pixDueDate.value || undefined,
      description: pixDescription.value || undefined,
    });

    pixResult.value = response.data;
    useAlert(t('KANBAN.DEAL.PIX.SUCCESS'));
  } catch (error) {
    const status = error?.response?.status;
    const message = error?.response?.data?.error;
    if (status === 422) {
      pixNotConfigured.value = true;
      pixErrorMessage.value = message || t('KANBAN.DEAL.PIX.NOT_CONFIGURED');
    } else {
      useAlert(message || t('KANBAN.DEAL.PIX.ERROR'));
    }
  } finally {
    isCreatingCharge.value = false;
  }
};

const copyPixPayload = async () => {
  if (!pixResult.value?.pix_payload) return;
  try {
    await copyTextToClipboard(pixResult.value.pix_payload);
    useAlert(t('KANBAN.DEAL.PIX.COPIED'));
  } catch (error) {
    useAlert(t('KANBAN.DEAL.PIX.COPY_ERROR'));
  }
};

// Quick stage move
const handleMoveToStage = async stageName => {
  showStageDropdown.value = false;
  emit('moveToStage', {
    conversation: props.conversation,
    mapping: props.mapping,
    newStage: stageName,
  });
};

const loadActivities = async () => {
  if (!props.mapping?.id) return;

  try {
    isLoadingActivities.value = true;

    const response = await KanbanAPI.getDealActivities(
      props.funnel.id,
      props.conversation.id
    );

    activities.value = response.data?.activities || [];
  } catch {
    // Silent fail for activities
  } finally {
    isLoadingActivities.value = false;
  }
};

const toggleChecklistItem = async item => {
  try {
    const newStatus = !item.done;

    await KanbanAPI.updateChecklistItem(
      props.funnel.id,
      props.conversation.id,
      item.id,
      { done: newStatus }
    );

    emit('update');
  } catch (error) {
    // Error handled by useAlert
    useAlert(t('KANBAN.DEAL.CHECKLIST_UPDATE_ERROR'));
  }
};

const addNote = async () => {
  if (!newNote.value.trim()) return;

  try {
    isAddingNote.value = true;

    await KanbanAPI.addDealNote(
      props.funnel.id,
      props.conversation.id,
      newNote.value
    );

    newNote.value = '';
    useAlert(t('KANBAN.DEAL.NOTE_ADDED'));
    emit('update');
    loadActivities();
  } catch (error) {
    // Error handled by useAlert
    useAlert(t('KANBAN.DEAL.NOTE_ADD_ERROR'));
  } finally {
    isAddingNote.value = false;
  }
};

const formatDate = dateString => {
  if (!dateString) return '';

  const date = normalizeDate(dateString);
  // Forçar formato dd/mm/yyyy usando pt-BR para consistência
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeTime = dateString => {
  if (!dateString) return '';

  const date = normalizeDate(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('KANBAN.DEAL.TIME.JUST_NOW');
  if (diffMins < 60)
    return t('KANBAN.DEAL.TIME.MINUTES_AGO', { count: diffMins });
  if (diffHours < 24)
    return t('KANBAN.DEAL.TIME.HOURS_AGO', { count: diffHours });
  if (diffDays < 7) return t('KANBAN.DEAL.TIME.DAYS_AGO', { count: diffDays });

  return formatDate(dateString);
};

const getActivityIcon = type => {
  const icons = {
    deal_created: 'i-lucide-plus-circle',
    deal_won: 'i-lucide-trophy',
    deal_lost: 'i-lucide-x-circle',
    deal_reopened: 'i-lucide-refresh-cw',
    stage_changed: 'i-lucide-arrow-right',
    value_updated: 'i-lucide-dollar-sign',
    probability_updated: 'i-lucide-percent',
    note_added: 'i-lucide-sticky-note',
    checklist_updated: 'i-lucide-check-circle',
    agent_assigned: 'i-lucide-user-plus',
    agent_removed: 'i-lucide-user-minus',
  };
  return icons[type] || 'i-lucide-info';
};

const getActivityColor = type => {
  const colors = {
    deal_won: 'text-emerald-500',
    deal_lost: 'text-red-500',
    deal_created: 'text-n-blue-text',
    deal_reopened: 'text-amber-500',
    stage_changed: 'text-n-blue-text',
    value_updated: 'text-amber-500',
    probability_updated: 'text-purple-500',
    note_added: 'text-violet-500',
    checklist_updated: 'text-emerald-500',
    agent_assigned: 'text-sky-500',
    agent_removed: 'text-orange-500',
  };
  return colors[type] || 'text-n-slate-11';
};

const getActivityBgColor = type => {
  const colors = {
    deal_won: 'bg-emerald-500/10',
    deal_lost: 'bg-red-500/10',
    deal_created: 'bg-n-blue-3',
    deal_reopened: 'bg-amber-500/10',
    stage_changed: 'bg-n-blue-3',
    value_updated: 'bg-amber-500/10',
    probability_updated: 'bg-purple-500/10',
    note_added: 'bg-violet-500/10',
    checklist_updated: 'bg-emerald-500/10',
    agent_assigned: 'bg-sky-500/10',
    agent_removed: 'bg-orange-500/10',
  };
  return colors[type] || 'bg-n-alpha-2';
};

// Watch for show changes
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      loadActivities();
    }
  }
);

// Load activities on mount if visible
onMounted(() => {
  if (props.show) {
    loadActivities();
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex justify-end"
        @click.self="handleClose"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-n-alpha-3" @click="handleClose" />

        <!-- Sidebar -->
        <Transition
          enter-active-class="transition-transform duration-300 ease-out"
          leave-active-class="transition-transform duration-200 ease-in"
          enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
          leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
        >
          <div
            v-if="show"
            class="relative w-full max-w-md h-full bg-n-solid-2 shadow-xl overflow-hidden flex flex-col ltr:border-l rtl:border-r border-n-weak"
          >
            <!-- Header -->
            <div
              class="flex items-center justify-between px-4 py-2 border-b border-n-weak h-14 flex-shrink-0"
            >
              <div class="flex items-center gap-2">
                <!-- Status Badge -->
                <span
                  class="px-2 py-0.5 rounded-md text-xs font-medium flex items-center gap-1"
                  :class="dealStatusConfig.class"
                >
                  <Icon :icon="dealStatusConfig.icon" class="size-3" />
                  {{ dealStatusConfig.label }}
                </span>

                <!-- Stage Badge with Dropdown -->
                <div class="relative">
                  <button
                    class="px-2 py-0.5 rounded-md text-xs font-medium bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 transition-colors flex items-center gap-1"
                    :disabled="dealStatus !== 'open'"
                    @click="showStageDropdown = !showStageDropdown"
                  >
                    {{ currentStageLabel }}
                    <Icon
                      v-if="dealStatus === 'open'"
                      icon="i-lucide-chevron-down"
                      class="size-3"
                    />
                  </button>

                  <!-- Stage Dropdown -->
                  <Transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                  >
                    <div
                      v-if="showStageDropdown && availableStages.length > 0"
                      class="absolute left-0 top-full mt-1 w-48 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg z-10 py-1"
                    >
                      <button
                        v-for="stage in availableStages"
                        :key="stage.name"
                        class="w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors flex items-center gap-2"
                        @click="handleMoveToStage(stage.name)"
                      >
                        <Icon icon="i-lucide-arrow-right" class="size-3.5" />
                        {{ stage.label || stage.name }}
                      </button>
                    </div>
                  </Transition>
                </div>

                <!-- Age Indicator -->
                <span
                  v-if="dealAge.days > 0"
                  v-tooltip="
                    t('KANBAN.DEAL.DETAILS.CREATED_AGO', { days: dealAge.days })
                  "
                  class="px-2 py-0.5 rounded-md text-xs font-medium flex items-center gap-1"
                  :class="dealAgeConfig.class"
                >
                  <Icon :icon="dealAgeConfig.icon" class="size-3" />
                  {{ dealAge.days }}{{ t('KANBAN.AGING.DAY').charAt(0) }}
                </span>
              </div>

              <div class="flex items-center gap-1">
                <!-- Win/Lose Buttons (only if status is open) -->
                <template v-if="dealStatus === 'open'">
                  <Button
                    v-tooltip="t('KANBAN.DEAL.WIN.BUTTON_TOOLTIP')"
                    icon="i-lucide-trophy"
                    ghost
                    teal
                    sm
                    @click="handleWinDeal"
                  />
                  <Button
                    v-tooltip="t('KANBAN.DEAL.LOSE.BUTTON_TOOLTIP')"
                    icon="i-lucide-x-circle"
                    ghost
                    ruby
                    sm
                    @click="handleLoseDeal"
                  />
                </template>

                <!-- Close Button -->
                <Button
                  v-tooltip="$t('GENERAL.CLOSE')"
                  icon="i-lucide-x"
                  ghost
                  slate
                  sm
                  @click="handleClose"
                />
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto">
              <!-- Contact Section -->
              <div class="p-4 border-b border-n-weak">
                <div class="flex items-start gap-3">
                  <!-- Avatar -->
                  <div class="flex-shrink-0">
                    <img
                      v-if="contactAvatar"
                      :src="contactAvatar"
                      :alt="contactName"
                      class="w-12 h-12 rounded-full object-cover"
                    />
                    <div
                      v-else
                      class="w-12 h-12 rounded-full bg-n-alpha-2 flex items-center justify-center text-base font-semibold text-n-slate-11"
                    >
                      {{ contactInitials }}
                    </div>
                  </div>

                  <!-- Contact Info -->
                  <div class="flex-1 min-w-0">
                    <h3
                      class="text-base font-semibold text-n-slate-12 truncate"
                    >
                      {{ contactName }}
                    </h3>
                    <div class="mt-1 space-y-0.5">
                      <a
                        v-if="contactEmail"
                        :href="`mailto:${contactEmail}`"
                        class="flex items-center gap-1.5 text-sm text-n-slate-11 hover:text-n-blue-text transition-colors"
                      >
                        <Icon icon="i-lucide-mail" class="size-3.5" />
                        <span class="truncate">{{ contactEmail }}</span>
                      </a>
                      <a
                        v-if="contactPhone"
                        :href="`tel:${contactPhone}`"
                        class="flex items-center gap-1.5 text-sm text-n-slate-11 hover:text-n-blue-text transition-colors"
                      >
                        <Icon icon="i-lucide-phone" class="size-3.5" />
                        {{ contactPhone }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deal Info Section (Creation date, Expected close, Tags) -->
              <div class="p-4 border-b border-n-weak">
                <div class="grid grid-cols-2 gap-3">
                  <!-- Creation Date -->
                  <div class="p-3 rounded-lg bg-n-alpha-1">
                    <div class="flex items-center gap-1.5 mb-1">
                      <Icon
                        icon="i-lucide-calendar-plus"
                        class="size-3.5 text-n-slate-10"
                      />
                      <span class="text-xs text-n-slate-10">{{
                        t('KANBAN.DEAL.DETAILS.CREATED_AT')
                      }}</span>
                    </div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{ formatDate(dealCreatedAt).split(',')[0] }}
                    </p>
                    <p
                      class="text-xs mt-0.5 flex items-center gap-1"
                      :class="dealAgeConfig.class"
                    >
                      <Icon :icon="dealAgeConfig.icon" class="size-3" />
                      {{
                        t('KANBAN.DEAL.DETAILS.DAYS_IN_PIPELINE', {
                          days: dealAge.days,
                        })
                      }}
                    </p>
                  </div>

                  <!-- Expected Close Date -->
                  <div class="p-3 rounded-lg bg-n-alpha-1">
                    <div class="flex items-center gap-1.5 mb-1">
                      <Icon
                        icon="i-lucide-calendar-check"
                        class="size-3.5 text-n-slate-10"
                      />
                      <span class="text-xs text-n-slate-10">{{
                        t('KANBAN.DEAL.DETAILS.EXPECTED_CLOSE')
                      }}</span>
                    </div>
                    <div v-if="!isEditingExpectedClose">
                      <p
                        v-if="expectedCloseDate"
                        class="text-sm font-medium"
                        :class="isOverdue ? 'text-red-500' : 'text-n-slate-12'"
                      >
                        {{ formatDate(expectedCloseDate).split(' ')[0] }}
                        <span
                          v-if="isOverdue"
                          class="text-xs ml-1 text-red-500"
                        >
                          {{ t('KANBAN.DEAL.DETAILS.OVERDUE') }}
                        </span>
                      </p>
                      <button
                        v-else
                        class="text-sm text-n-blue-text hover:underline"
                        @click="startEditExpectedClose"
                      >
                        {{ t('KANBAN.DEAL.DETAILS.SET_DATE') }}
                      </button>
                      <button
                        v-if="expectedCloseDate"
                        class="text-xs text-n-slate-10 hover:text-n-slate-12 mt-1 block"
                        @click="startEditExpectedClose"
                      >
                        {{ t('KANBAN.DEAL.DETAILS.CHANGE') }}
                      </button>
                    </div>
                    <div v-else class="space-y-2">
                      <input
                        v-model="editedExpectedClose"
                        type="date"
                        class="w-full px-2 py-1 rounded border border-n-strong bg-n-solid-3 text-n-slate-12 text-sm"
                      />
                      <div class="flex gap-1">
                        <Button
                          icon="i-lucide-check"
                          ghost
                          teal
                          xs
                          @click="saveExpectedClose"
                        />
                        <Button
                          icon="i-lucide-x"
                          ghost
                          slate
                          xs
                          @click="cancelEditExpectedClose"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tags -->
                <div v-if="dealTags.length > 0" class="mt-3">
                  <div class="flex items-center gap-1.5 mb-2">
                    <Icon
                      icon="i-lucide-tags"
                      class="size-3.5 text-n-slate-10"
                    />
                    <span
                      class="text-xs text-n-slate-10 uppercase tracking-wide"
                    >
                      {{ t('KANBAN.DEAL.DETAILS.TAGS') }}
                    </span>
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    <span
                      v-for="tag in dealTags"
                      :key="tag.id"
                      class="px-2 py-0.5 rounded-full text-xs font-medium"
                      :style="{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                      }"
                    >
                      {{ tag.title }}
                    </span>
                  </div>
                </div>

                <!-- Loss Reason (for lost deals) -->
                <div
                  v-if="dealStatus === 'lost' && lossReason"
                  class="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
                >
                  <div class="flex items-center gap-1.5 mb-1">
                    <Icon
                      icon="i-lucide-x-circle"
                      class="size-3.5 text-red-500"
                    />
                    <span class="text-xs text-red-400 uppercase tracking-wide">
                      {{ t('KANBAN.DEAL.DETAILS.LOSS_REASON') }}
                    </span>
                  </div>
                  <p class="text-sm font-medium text-red-400">
                    {{ lossReason }}
                  </p>
                  <p v-if="lossNotes" class="text-xs text-red-400/80 mt-1">
                    {{ lossNotes }}
                  </p>
                </div>

                <!-- Won Info (for won deals) -->
                <div
                  v-if="dealStatus === 'won'"
                  class="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20"
                >
                  <div class="flex items-center gap-1.5 mb-1">
                    <Icon
                      icon="i-lucide-trophy"
                      class="size-3.5 text-emerald-500"
                    />
                    <span
                      class="text-xs text-emerald-400 uppercase tracking-wide"
                      >{{ t('KANBAN.DEAL.DETAILS.DEAL_WON') }}</span
                    >
                  </div>
                  <p class="text-sm font-medium text-emerald-400">
                    {{ formattedDealValue }}
                  </p>
                  <p v-if="wonReason" class="text-xs text-emerald-400/80 mt-1">
                    {{ t('KANBAN.DEAL.DETAILS.WON_REASON') }}: {{ wonReason }}
                  </p>
                </div>
              </div>

              <!-- Deal Value Section -->
              <div class="p-4 border-b border-n-weak">
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-dollar-sign" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.VALUE') }}
                </h4>

                <div
                  v-if="!isEditingValue"
                  class="flex items-center justify-between"
                >
                  <span class="text-xl font-bold text-n-slate-12">
                    {{ formattedDealValue }}
                  </span>
                  <Button
                    icon="i-lucide-pencil"
                    ghost
                    slate
                    xs
                    @click="startEditValue"
                  />
                </div>

                <div v-else class="flex items-center gap-2">
                  <div class="relative flex-1">
                    <span
                      class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-11 text-sm"
                    >
                      {{ currencySymbol }}
                    </span>
                    <input
                      v-model.number="editedValue"
                      type="number"
                      step="0.01"
                      min="0"
                      class="w-full pl-10 pr-3 py-1.5 rounded-lg border border-n-strong bg-n-solid-3 text-n-slate-12 text-sm focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none"
                    />
                  </div>
                  <Button
                    icon="i-lucide-check"
                    ghost
                    teal
                    sm
                    :disabled="isLoading"
                    @click="saveValue"
                  />
                  <Button
                    icon="i-lucide-x"
                    ghost
                    slate
                    sm
                    @click="cancelEditValue"
                  />
                </div>

                <!-- Generate PIX charge -->
                <div v-if="!isEditingValue && dealValue > 0" class="mt-3">
                  <Button
                    :label="t('KANBAN.DEAL.PIX.GENERATE_BUTTON')"
                    icon="i-lucide-qr-code"
                    teal
                    faded
                    sm
                    class="w-full"
                    @click="openPixModal"
                  />
                </div>
              </div>

              <!-- Probability Section -->
              <div class="p-4 border-b border-n-weak">
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-percent" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.PROBABILITY') }}
                </h4>

                <div
                  v-if="!isEditingProbability"
                  class="flex items-center justify-between"
                >
                  <div class="flex-1">
                    <div class="flex items-center justify-between mb-1">
                      <span class="text-base font-semibold text-n-slate-12">
                        {{ probability }}%
                      </span>
                    </div>
                    <div
                      class="h-1.5 bg-n-alpha-2 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full rounded-full transition-all"
                        :class="[
                          probability >= 70
                            ? 'bg-n-teal-9'
                            : probability >= 40
                              ? 'bg-n-amber-9'
                              : 'bg-n-ruby-9',
                        ]"
                        :style="{ width: `${probability}%` }"
                      />
                    </div>
                  </div>
                  <Button
                    icon="i-lucide-pencil"
                    ghost
                    slate
                    xs
                    class="ml-3"
                    @click="startEditProbability"
                  />
                </div>

                <div v-else class="flex items-center gap-2">
                  <input
                    v-model.number="editedProbability"
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    class="flex-1 h-1.5 bg-n-alpha-2 rounded-full appearance-none cursor-pointer"
                  />
                  <span
                    class="w-10 text-center font-medium text-n-slate-12 text-sm"
                  >
                    {{ editedProbability }}%
                  </span>
                  <Button
                    icon="i-lucide-check"
                    ghost
                    teal
                    sm
                    :disabled="isLoading"
                    @click="saveProbability"
                  />
                  <Button
                    icon="i-lucide-x"
                    ghost
                    slate
                    sm
                    @click="cancelEditProbability"
                  />
                </div>
              </div>

              <!-- Checklist Section -->
              <div
                v-if="checklist.length > 0"
                class="p-4 border-b border-n-weak"
              >
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center justify-between"
                >
                  <span class="flex items-center gap-1.5">
                    <Icon icon="i-lucide-check-circle" class="size-3.5" />
                    {{ t('KANBAN.DEAL.DETAILS.CHECKLIST') }}
                  </span>
                  <span class="text-n-slate-11 normal-case">
                    {{ checklistProgress.done }}/{{ checklistProgress.total }}
                  </span>
                </h4>

                <div class="space-y-1">
                  <label
                    v-for="item in checklist"
                    :key="item.id"
                    class="flex items-center gap-2.5 p-2 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      :checked="item.done"
                      class="w-4 h-4 rounded border-n-strong text-n-brand focus:ring-n-brand bg-n-solid-3"
                      @change="toggleChecklistItem(item)"
                    />
                    <span
                      class="text-sm"
                      :class="[
                        item.done
                          ? 'text-n-slate-10 line-through'
                          : 'text-n-slate-12',
                      ]"
                    >
                      {{ item.text }}
                    </span>
                  </label>
                </div>
              </div>

              <!-- Products Section -->
              <div
                v-if="products.length > 0"
                class="p-4 border-b border-n-weak"
              >
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-tag" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.PRODUCTS') }}
                </h4>

                <div class="space-y-1.5">
                  <div
                    v-for="product in products"
                    :key="product.id"
                    class="flex items-center justify-between p-2 rounded-lg bg-n-alpha-1"
                  >
                    <span class="text-sm text-n-slate-12">
                      {{ product.name || product.product_name }}
                    </span>
                    <span class="text-sm font-medium text-n-slate-12">
                      {{
                        new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: currency.value,
                        }).format(product.amount || 0)
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Agents Section -->
              <div
                v-if="assignedAgents.length > 0"
                class="p-4 border-b border-n-weak"
              >
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-users" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.AGENTS') }}
                </h4>

                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="agent in assignedAgents"
                    :key="agent.id"
                    class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-n-alpha-1"
                  >
                    <img
                      v-if="agent.avatar_url"
                      :src="agent.avatar_url"
                      :alt="agent.name"
                      class="w-5 h-5 rounded-full object-cover"
                    />
                    <div
                      v-else
                      class="w-5 h-5 rounded-full bg-n-brand text-white text-xs flex items-center justify-center"
                    >
                      {{ agent.name?.charAt(0) }}
                    </div>
                    <span class="text-sm text-n-slate-12">
                      {{ agent.name }}
                    </span>
                    <span
                      v-if="agent.role === 'primary'"
                      class="text-xs text-n-blue-text"
                    >
                      ({{ t('KANBAN.DEAL.DETAILS.PRIMARY') }})
                    </span>
                  </div>
                </div>
              </div>

              <!-- Notes Section -->
              <div class="p-4 border-b border-n-weak">
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-sticky-note" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.NOTES') }}
                </h4>

                <!-- Add Note Form -->
                <div class="mb-3">
                  <textarea
                    v-model="newNote"
                    rows="2"
                    :placeholder="t('KANBAN.DEAL.DETAILS.ADD_NOTE_PLACEHOLDER')"
                    class="w-full px-3 py-2 rounded-lg border border-n-strong bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 text-sm focus:ring-2 focus:ring-n-brand focus:border-n-brand resize-none outline-none"
                  />
                  <div class="flex justify-end mt-2">
                    <Button
                      :label="t('KANBAN.DEAL.DETAILS.ADD_NOTE')"
                      icon="i-lucide-plus"
                      size="xs"
                      :is-loading="isAddingNote"
                      :disabled="!newNote.trim() || isAddingNote"
                      @click="addNote"
                    />
                  </div>
                </div>

                <!-- Notes from Activities -->
                <div class="space-y-2">
                  <div
                    v-for="activity in activities.filter(
                      a => a.activity_type === 'note_added'
                    )"
                    :key="activity.id"
                    class="p-3 rounded-lg bg-n-alpha-1"
                  >
                    <p class="text-sm text-n-slate-12">
                      {{ activity.metadata?.content }}
                    </p>
                    <div
                      class="flex items-center gap-1.5 mt-2 text-xs text-n-slate-10"
                    >
                      <span>{{ activity.user_name || 'Sistema' }}</span>
                      <span>·</span>
                      <span>{{ formatRelativeTime(activity.created_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- History Section -->
              <div class="p-4">
                <h4
                  class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-3 flex items-center gap-1.5"
                >
                  <Icon icon="i-lucide-history" class="size-3.5" />
                  {{ t('KANBAN.DEAL.DETAILS.HISTORY') }}
                </h4>

                <div
                  v-if="isLoadingActivities"
                  class="flex justify-center py-4"
                >
                  <Icon
                    icon="i-lucide-loader-2"
                    class="size-5 animate-spin text-n-slate-10"
                  />
                </div>

                <div v-else class="relative">
                  <!-- Timeline line -->
                  <div
                    v-if="stageHistory.length > 0 || activities.length > 0"
                    class="absolute left-3.5 top-3 bottom-3 w-px bg-n-weak"
                  />

                  <div class="space-y-3">
                    <!-- Stage History -->
                    <div
                      v-for="(entry, index) in stageHistory.slice().reverse()"
                      :key="`stage-${index}`"
                      class="flex items-start gap-3 relative"
                    >
                      <div class="flex-shrink-0 z-10">
                        <div
                          class="w-7 h-7 rounded-full bg-n-blue-3 flex items-center justify-center ring-4 ring-n-solid-2"
                        >
                          <Icon
                            icon="i-lucide-git-branch"
                            class="size-3.5 text-n-blue-text"
                          />
                        </div>
                      </div>
                      <div class="flex-1 min-w-0 pt-0.5">
                        <p class="text-sm text-n-slate-12">
                          <span class="font-medium">{{
                            t('KANBAN.DEAL.HISTORY.MOVED')
                          }}</span>
                          <span
                            class="px-1.5 py-0.5 mx-1 rounded bg-n-alpha-2 text-xs"
                            >{{ entry.from }}</span
                          >
                          <Icon
                            icon="i-lucide-arrow-right"
                            class="size-3 inline mx-1"
                          />
                          <span
                            class="px-1.5 py-0.5 mx-1 rounded bg-n-blue-3 text-n-blue-text text-xs"
                            >{{ entry.to }}</span
                          >
                        </p>
                        <p class="text-xs text-n-slate-10 mt-1">
                          {{ formatRelativeTime(entry.moved_at) }}
                        </p>
                      </div>
                    </div>

                    <!-- Other Activities -->
                    <div
                      v-for="activity in activities.filter(
                        a => a.activity_type !== 'note_added'
                      )"
                      :key="activity.id"
                      class="flex items-start gap-3 relative"
                    >
                      <div class="flex-shrink-0 z-10">
                        <div
                          class="w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-n-solid-2"
                          :class="getActivityBgColor(activity.activity_type)"
                        >
                          <Icon
                            :icon="getActivityIcon(activity.activity_type)"
                            class="size-3.5"
                            :class="[getActivityColor(activity.activity_type)]"
                          />
                        </div>
                      </div>
                      <div class="flex-1 min-w-0 pt-0.5">
                        <p class="text-sm text-n-slate-12">
                          {{ activity.description || activity.activity_type }}
                        </p>
                        <div
                          class="flex items-center gap-2 mt-1 text-xs text-n-slate-10"
                        >
                          <span class="flex items-center gap-1">
                            <Icon icon="i-lucide-user" class="size-3" />
                            {{ activity.user_name || 'Sistema' }}
                          </span>
                          <span>·</span>
                          <span>{{
                            formatRelativeTime(activity.created_at)
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Empty State -->
                    <div
                      v-if="
                        stageHistory.length === 0 && activities.length === 0
                      "
                      class="text-center py-8"
                    >
                      <div
                        class="w-12 h-12 rounded-full bg-n-alpha-1 flex items-center justify-center mx-auto"
                      >
                        <Icon
                          icon="i-lucide-history"
                          class="size-6 text-n-slate-9"
                        />
                      </div>
                      <p class="text-sm text-n-slate-10 mt-3">
                        {{ t('KANBAN.DEAL.HISTORY.EMPTY') }}
                      </p>
                      <p class="text-xs text-n-slate-9 mt-1">
                        {{ t('KANBAN.DEAL.HISTORY.EMPTY_DESC') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>

  <!-- PIX Charge Modal -->
  <Teleport to="body">
    <div
      v-if="showPixModal"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      @click.self="closePixModal"
    >
      <div
        class="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-2xl overflow-hidden"
      >
        <!-- Header -->
        <div
          class="px-6 py-4 bg-n-teal-9 text-white flex items-center justify-between"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
            >
              <Icon icon="i-lucide-qr-code" class="size-5" />
            </div>
            <h3 class="text-lg font-semibold">
              {{ t('KANBAN.DEAL.PIX.TITLE') }}
            </h3>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            @click="closePixModal"
          >
            <Icon icon="i-lucide-x" class="size-5" />
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-4">
          <!-- Not configured warning -->
          <div
            v-if="pixNotConfigured"
            class="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-2"
          >
            <div class="flex items-start gap-2">
              <Icon
                icon="i-lucide-alert-triangle"
                class="size-5 text-amber-500 mt-0.5"
              />
              <p class="text-sm text-amber-700 dark:text-amber-300">
                {{ pixErrorMessage || t('KANBAN.DEAL.PIX.NOT_CONFIGURED') }}
              </p>
            </div>
            <router-link
              :to="{
                name: 'settings_integrations_asaas',
                params: { accountId: currentAccountId },
              }"
              class="inline-flex items-center gap-1 text-sm font-medium text-n-teal-11 hover:underline"
            >
              <Icon icon="i-lucide-settings" class="size-4" />
              {{ t('KANBAN.DEAL.PIX.CONFIGURE_LINK') }}
            </router-link>
          </div>

          <!-- Form (before charge created) -->
          <template v-if="!pixResult">
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ t('KANBAN.DEAL.PIX.VALUE') }}
              </label>
              <div class="flex items-center gap-2">
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <span class="text-base font-semibold text-slate-500">R$</span>
                <input
                  v-model.number="pixValue"
                  type="number"
                  step="0.01"
                  min="0"
                  class="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-n-teal-9 focus:border-n-teal-9"
                />
              </div>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ t('KANBAN.DEAL.PIX.DUE_DATE') }}
                <span class="text-slate-400 font-normal"
                  >({{ t('KANBAN.DEAL.OPTIONAL') }})</span
                >
              </label>
              <input
                v-model="pixDueDate"
                type="date"
                class="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-n-teal-9 focus:border-n-teal-9"
              />
            </div>

            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ t('KANBAN.DEAL.PIX.DESCRIPTION') }}
                <span class="text-slate-400 font-normal"
                  >({{ t('KANBAN.DEAL.OPTIONAL') }})</span
                >
              </label>
              <input
                v-model="pixDescription"
                type="text"
                :placeholder="t('KANBAN.DEAL.PIX.DESCRIPTION_PLACEHOLDER')"
                class="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-n-teal-9 focus:border-n-teal-9"
              />
            </div>
          </template>

          <!-- Result (charge created) -->
          <template v-else>
            <div
              class="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
            >
              <Icon icon="i-lucide-clock" class="size-4 text-amber-500" />
              <span class="text-sm text-amber-700 dark:text-amber-300">
                {{ t('KANBAN.DEAL.PIX.WAITING_PAYMENT') }}
              </span>
            </div>

            <div v-if="pixResult.pix_qr_code" class="flex justify-center">
              <img
                :src="'data:image/png;base64,' + pixResult.pix_qr_code"
                :alt="t('KANBAN.DEAL.PIX.QR_CODE_ALT')"
                class="w-48 h-48 rounded-lg border border-slate-200 dark:border-slate-700"
              />
            </div>

            <div v-if="pixResult.pix_payload">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{ t('KANBAN.DEAL.PIX.PAYLOAD_LABEL') }}
              </label>
              <div
                class="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
              >
                <p
                  class="flex-1 text-xs text-slate-700 dark:text-slate-300 break-all font-mono"
                >
                  {{ pixResult.pix_payload }}
                </p>
                <Button
                  :label="t('KANBAN.DEAL.PIX.COPY')"
                  icon="i-lucide-copy"
                  teal
                  faded
                  xs
                  @click="copyPixPayload"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Footer -->
        <div
          class="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2"
        >
          <Button variant="faded" color="slate" @click="closePixModal">
            {{ pixResult ? t('GENERAL.CLOSE') : t('KANBAN.DEAL.CANCEL') }}
          </Button>
          <Button
            v-if="!pixResult"
            color="teal"
            icon="i-lucide-qr-code"
            :is-loading="isCreatingCharge"
            :disabled="isCreatingCharge"
            @click="createPixCharge"
          >
            {{ t('KANBAN.DEAL.PIX.GENERATE_CONFIRM') }}
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
